import { Pie, PieChart, Legend, Cell, Sector } from 'recharts';
import { useState } from 'react';
import './FacetFieldPiecharts.scss';
import { tokens } from '@fluentui/react-components';
import { FacetValue, FacetValues, FacetValuesCount } from '../../../types/Facet';

/**
 * The default number of facet values to display in a pie chart.
 */
const DEFAULT_FACET_VALUE_COUNT: number = 10;

interface FacetFieldPieChartsProps {
    selectedFacetValues: FacetValues<FacetValue>[];
    onChangeFacetPie: (facetName: string, facetValue: FacetValue) => void;
    fieldName: string;
    name: string;
    allFacetValues: FacetValuesCount
}

const FacetFieldPieCharts = ({
    selectedFacetValues,
    onChangeFacetPie,
    fieldName,
    name,
    allFacetValues,
}: FacetFieldPieChartsProps) => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const [showTooltip, setTooltipFlag] = useState(false);

    let pieChartName = name;

    if (pieChartName.indexOf('/') > 0) {
        pieChartName = pieChartName.split('/')[0];
    }

    const legendMouseEnter = (index: number) => {
        setActiveIndex(index);
        let elementDiv = document.getElementById('legend-' + fieldName + '-' + index);

        let scrollWidth = elementDiv?.scrollWidth;
        let offsetWidth = elementDiv?.offsetWidth;
        if (scrollWidth !== undefined && offsetWidth !== undefined && scrollWidth > offsetWidth) {
            setTooltipFlag(true);
        } else {
            setTooltipFlag(false);
        }
    };

    const legendMouseLeave = () => {
        setActiveIndex(-1);
    };

    type LegendPayload = {
        value: string;
        color: string;
        [key: string]: any;
    };

    interface CustomLegendProps {
        payload?: LegendPayload[];
    }

    const CustomLegend = (props: CustomLegendProps) => {
        const { payload } = props;
        const selectedFacetValue = selectedFacetValues.find((f) => f.facetName === fieldName);

        if (!payload || payload.length === 0) {
            return <div className="custom-legend">No legend data</div>;
        }
        return (
            <div className="custom-legend">
                {payload.map((entry, index) => {
                    const facetValue = allFacetValues.facetValues.find((fv) => fv.facetValue.getStringValue() === entry.value);
                    if (!facetValue) {
                        throw new Error(`Facet value not found for entry: ${entry.value}`);
                    }

                    return (
                        <div key={`legend-${index}`} style={{ color: entry.color }}>
                            <div style={{ display: 'inline-block', overflow: 'hidden' }}>
                                <span
                                    className={`legend-icon ${selectedFacetValue !== undefined && selectedFacetValue.facetValues.find((fv) => fv.getStringValue() === entry.value) !== undefined && 'legend-icon-big'
                                        }`}
                                >
                                    ●
                                </span>
                            </div>
                            <div id={`legend-${fieldName}-${index}`} className="legend-div" title={showTooltip ? entry.value : undefined}>
                                <span
                                    className={`legend-text ${activeIndex === index && 'legend-text-bold'}`}
                                    onMouseEnter={() => legendMouseEnter(index)}
                                    onMouseLeave={() => legendMouseLeave()}
                                    onClick={(e) => onChangeFacetPie(fieldName, facetValue.facetValue)}
                                >
                                    {facetValue.facetValue.getDisplayValue()}
                                </span>
                            </div>
                        </div>
                    );
                }
                )}
            </div>
        );
    };

    const renderActiveShape = (props: any) => {
        const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, value } = props;
        return (
            <g>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius + 10}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
                <text x={cx} y={cy} textAnchor="middle" fill={tokens.colorNeutralForeground1}>
                    {value}
                </text>
            </g>
        );
    };

    const getColor = (index: number, colorCount: number): string => {
        if (colorCount < 13) colorCount = 13;

        const stepSize = 360 / colorCount;
        const offset = 240;
        const hslValue = (offset + stepSize * index) % 360;

        return 'hsl(' + hslValue + ', 60%, 60%)';
    };

    const pieDataArr = allFacetValues.facetValues.slice(0, DEFAULT_FACET_VALUE_COUNT).map((fv) => ({
        name: fv.facetValue.getStringValue(),
        value: fv.count,
    }));

    const  onCPieClick = (e: any, fieldName: string): void => {
        if (!e || !e.name) return;
        const facetValueObj = allFacetValues.facetValues.find(fv => fv.facetValue.getStringValue() === e.name);
        if (facetValueObj) {
            onChangeFacetPie(fieldName, facetValueObj.facetValue);
        }
    }

    return (

        <div className="piechart-div">
            <h3 className="piechart-header">{pieChartName}</h3>
            {allFacetValues.facetValues.length === 0 && (
                <PieChart width={300} height={180}>
                    <Legend
                        iconType="circle"
                        iconSize={8}
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        wrapperStyle={{
                            padding: '0 0rem',
                            height: '50%',
                            width: '45%',
                            overflow: 'auto',
                            right: '-10px',
                        }}
                    />
                    <Pie
                        dataKey="value"
                        data={[{ name: 'No data available', value: 1, fill: '#eeee' }]}
                        blendStroke
                        className="focus:outline-none"
                        cx="50%"
                        cy="50%"
                        innerRadius="45%"
                        outerRadius="95%"
                    ></Pie>
                </PieChart>
            )}
            {allFacetValues.facetValues.length > 0 && (
                <PieChart width={300} height={180}>
                    <Legend
                        iconType="circle"
                        iconSize={8}
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        wrapperStyle={{
                            padding: '0 0rem',
                            height: '95%',
                            width: '40%',
                            overflowX: 'hidden',
                            right: '-10px',
                            whiteSpace: 'nowrap',
                            top: '5px',
                        }}
                        content={<CustomLegend payload={undefined} />}
                    />
                    <Pie
                        activeShape={renderActiveShape}
                        activeIndex={activeIndex}
                        startAngle={90}
                        animationDuration={550}
                        animationEasing="ease-in-out"
                        endAngle={-270}
                        dataKey="value"
                        data={pieDataArr}
                        blendStroke
                        className="focus:outline-none"
                        labelLine={false}
                        cx="50%"
                        cy="50%"
                        innerRadius="45%"
                        outerRadius="95%"
                        isAnimationActive={true}
                        minAngle={12}
                        onClick={(e) => onCPieClick(e, fieldName)}                        
                        onMouseEnter={(_, index) => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(-1)}
                    >
                        {pieDataArr.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getColor(index, pieDataArr.length)} />
                        ))}
                    </Pie>
                </PieChart>
            )}
        </div>
    );
};

export default FacetFieldPieCharts;
